/* prettier-ignore */

// HRS-2948: respaldo local de las frases de voz (bienvenida/muletillas).
// La fuente principal son las claves "voice.welcome|listening|thinking" de los langs
// (src/assets/i18n/<lang>.json fusionados con cms-iurban.com/langs); este mapa cubre
// clave ausente o idioma sin cargar, para que al TTS nunca llegue una clave cruda
// tipo "voice.welcome".
// Mantener el contenido sincronizado con los 18 JSON de src/assets/i18n y con
// angular-chatbot-v3 (src/app/service/fillerPhrases.i18n.ts).

import { pickByLanguage } from 'src/app/services/helpers/language.util';

export type FillerType = 'listening' | 'thinking' | 'welcome';
export type FillerSet = Record<FillerType, string[]>;

const ES: FillerSet = {
    listening: ['Ok', 'vale', 'ummmm'],
    thinking: ['Vale, entendido... déjame que piense un poco...', 'Interesante.... déjame que lo vea por si puedo ayudarte.', 'Perfecto... dame un segundo que mire para ser mas exactos', 'Lo entiendo, dame un momento que busque bien la información y te digo'],
    welcome: ['Hola, seré tu asistente hoy... cuéntame, te escucho', 'Hola. ¿qué tal?, seré tu asistente de turismo']
};

const EN: FillerSet = {
    listening: ['Okay', 'Right', 'Uh-huh'],
    thinking: ['Okay, got it... let me think for a moment...', 'Interesting... let me see if I can help you with that.', 'Perfect... give me a second to look that up.', 'I understand. One moment while I find the right information.'],
    welcome: ["Hi! I'll be your assistant today... go ahead, I'm listening.", "Hello! I'm your tourism assistant. How can I help you?"]
};

const CA: FillerSet = {
    listening: ["D'acord", 'val', 'mmmm'],
    thinking: ["D'acord, entesos... deixa'm pensar un moment...", "Interessant... deixa'm veure si et puc ajudar.", "Perfecte... dona'm un segon que ho miri.", 'Ho entenc. Un moment mentre busco la informació.'],
    welcome: ["Hola, seré el teu assistent avui... explica'm, t'escolto", 'Hola, què tal? Seré el teu assistent de turisme']
};

const VA: FillerSet = {
    listening: ["D'acord", 'val', 'mmmm'],
    thinking: ["D'acord, entés... deixa'm pensar un moment...", "Interessant... deixa'm vore si et puc ajudar.", "Perfecte... dona'm un segon que ho mire.", 'Ho entenc. Un moment mentre busque la informació.'],
    welcome: ["Hola, seré el teu assistent hui... conta'm, t'escolte", 'Hola, què tal? Seré el teu assistent de turisme']
};

const GL: FillerSet = {
    listening: ['Vale', 'de acordo', 'mmmm'],
    thinking: ['Vale, entendido... déixame pensar un momento...', 'Interesante... déixame ver se che podo axudar.', 'Perfecto... dáme un segundo que o mire.', 'Enténdoo. Un momento mentres busco a información.'],
    welcome: ['Ola, serei o teu asistente hoxe... cóntame, escóitote', 'Ola, que tal? Serei o teu asistente de turismo']
};

const EU: FillerSet = {
    listening: ['Ados', 'bale', 'mmmm'],
    thinking: ['Ados, ulertuta... utzi pixka bat pentsatzen...', 'Interesgarria... ea lagundu zaitzakedan begiratuko dut.', 'Primeran... eman segundo bat begiratzeko.', 'Ulertzen dut. Momentu bat, informazioa bilatzen ari naiz.'],
    welcome: ['Kaixo, zure laguntzailea izango naiz gaur... esadazu, entzuten dizut', 'Kaixo, zer moduz? Zure turismo-laguntzailea izango naiz']
};

const FR: FillerSet = {
    listening: ["D'accord", 'oui', 'hmmm'],
    thinking: ["D'accord, compris... laissez-moi réfléchir un instant...", 'Intéressant... je regarde si je peux vous aider.', 'Parfait... donnez-moi une seconde pour vérifier.', 'Je comprends. Un instant, je cherche la bonne information.'],
    welcome: ["Bonjour, je serai votre assistant aujourd'hui... dites-moi, je vous écoute", 'Bonjour ! Je serai votre assistant touristique']
};

const DE: FillerSet = {
    listening: ['Okay', 'gut', 'hmmm'],
    thinking: ['Okay, verstanden... lassen Sie mich kurz nachdenken...', 'Interessant... ich schaue, ob ich Ihnen helfen kann.', 'Perfekt... geben Sie mir eine Sekunde, das nachzusehen.', 'Verstanden. Einen Moment, ich suche die passende Information.'],
    welcome: ['Hallo, ich bin heute Ihr Assistent... erzählen Sie, ich höre zu', 'Hallo! Ich bin Ihr Tourismus-Assistent']
};

const IT: FillerSet = {
    listening: ['Ok', 'va bene', 'mmmm'],
    thinking: ['Ok, capito... fammi pensare un attimo...', 'Interessante... vediamo se posso aiutarti.', 'Perfetto... dammi un secondo per controllare.', 'Capisco. Un momento, cerco le informazioni giuste.'],
    welcome: ['Ciao, sarò il tuo assistente oggi... dimmi, ti ascolto', 'Ciao, come va? Sarò il tuo assistente turistico']
};

const PT: FillerSet = {
    listening: ['Ok', 'certo', 'hummm'],
    thinking: ['Ok, entendido... deixa-me pensar um pouco...', 'Interessante... deixa-me ver se posso ajudar.', 'Perfeito... dá-me um segundo para verificar.', 'Compreendo. Um momento enquanto procuro a informação.'],
    welcome: ['Olá, serei o teu assistente hoje... conta-me, estou a ouvir', 'Olá, tudo bem? Serei o teu assistente de turismo']
};

const NL: FillerSet = {
    listening: ['Oké', 'goed', 'hmmm'],
    thinking: ['Oké, begrepen... laat me even nadenken...', 'Interessant... ik kijk of ik je kan helpen.', 'Perfect... geef me een seconde om het op te zoeken.', 'Ik begrijp het. Een moment, ik zoek de juiste informatie.'],
    welcome: ['Hallo, ik ben vandaag je assistent... vertel, ik luister', 'Hallo! Ik ben je toerisme-assistent']
};

const FI: FillerSet = {
    listening: ['Okei', 'selvä', 'hmmm'],
    thinking: ['Okei, selvä... anna kun mietin hetken...', 'Mielenkiintoista... katsotaan, voinko auttaa.', 'Hienoa... pieni hetki, tarkistan asian.', 'Ymmärrän. Pieni hetki, etsin oikeat tiedot.'],
    welcome: ['Hei, olen tänään avustajasi... kerro, kuuntelen', 'Hei! Olen matkailuavustajasi']
};

const RU: FillerSet = {
    listening: ['Хорошо', 'ладно', 'ммм'],
    thinking: ['Хорошо, понятно... дайте мне немного подумать...', 'Интересно... посмотрю, чем могу помочь.', 'Отлично... секунду, я уточню.', 'Понимаю. Минутку, я найду нужную информацию.'],
    welcome: ['Здравствуйте, сегодня я буду вашим помощником... расскажите, я вас слушаю', 'Здравствуйте! Я ваш туристический помощник']
};

const JA: FillerSet = {
    listening: ['はい', 'ええ', 'うーん'],
    thinking: ['はい、わかりました…少し考えさせてください…', 'なるほど…お手伝いできるか確認しますね。', 'かしこまりました…少々お待ちください。', '承知しました。正しい情報をお探ししますので、少々お待ちください。'],
    welcome: ['こんにちは。本日はあなたのアシスタントです…どうぞ、お聞きしています', 'こんにちは!観光アシスタントです']
};

const KO: FillerSet = {
    listening: ['네', '알겠어요', '음…'],
    thinking: ['네, 알겠습니다… 잠시 생각해 볼게요…', '흥미롭네요… 도와드릴 수 있는지 확인해 볼게요.', '좋아요… 잠시만요, 확인해 보겠습니다.', '알겠습니다. 정확한 정보를 찾는 동안 잠시만 기다려 주세요.'],
    welcome: ['안녕하세요, 오늘 여러분의 어시스턴트입니다… 말씀하세요, 듣고 있어요', '안녕하세요! 저는 여러분의 관광 어시스턴트입니다']
};

const ZH: FillerSet = {
    listening: ['好的', '嗯', '唔……'],
    thinking: ['好的,明白了……让我想一想……', '有意思……我看看能不能帮到您。', '好的……请稍等,我查一下。', '明白了。请稍等,我为您查找相关信息。'],
    welcome: ['您好,今天我将是您的助手……请讲,我在听', '您好!我是您的旅游助手']
};

// Mismos 18 códigos que sirven los langs del CMS; 'kr' y 'ch' son alias históricos.
export const FILLER_FALLBACK: Record<string, FillerSet> = {
    es: ES, en: EN, ca: CA, va: VA, gl: GL, eu: EU, fr: FR, de: DE, it: IT,
    pt: PT, nl: NL, fi: FI, ru: RU, ja: JA, ko: KO, kr: KO, zh: ZH, ch: ZH,
};

export function getFillerFallback(lang: string, type: FillerType): string[] {
    // Con variantes regionales (HRS-2934) el acceso exacto fallaba y el chat
    // soltaba las frases en español mientras el usuario hablaba en portugués.
    return (pickByLanguage(FILLER_FALLBACK, lang) ?? FILLER_FALLBACK['es'])[type];
}
